import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';

export interface Alert {
    id: number;
    tipo: 'sin_cobertura' | 'beacon_offline' | 'agente_desconectado' | 'agente_localizado';
    mensaje: string;
    referencia_id: number;
    referencia_tipo: string;
    planta_id?: number | null;
    leida: boolean;
    created_at: string;
}

interface AlertsResponse {
    data: Alert[];
    sin_leer: number;
}

export function useAlerts(filters?: { tipo?: string; leida?: string }) {
    return useQuery<AlertsResponse>({
        queryKey: ['alertas', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters?.tipo) params.set('tipo', filters.tipo);
            if (filters?.leida !== undefined) params.set('leida', filters.leida);
            const { data } = await client.get(`/alertas?${params}`);
            return data;
        },
        refetchInterval: 30000,
    });
}

export function useUnreadCount() {
    const qc = useQueryClient();
    const prevCount = useRef<number | undefined>(undefined);

    const query = useQuery<number>({
        queryKey: ['alertas-count'],
        queryFn: async () => {
            const { data } = await client.get('/alertas?leida=false&limit=0');
            return data.sin_leer;
        },
        refetchInterval: 15000,
    });

    useEffect(() => {
        const count = query.data;
        if (count !== undefined && prevCount.current !== undefined && count > prevCount.current) {
            qc.invalidateQueries({ queryKey: ['posiciones'] });
        }
        prevCount.current = count;
    }, [query.data, qc]);

    return query;
}

export function useMarkAsRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => client.put(`/alertas/${id}/leer`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['alertas'] });
            qc.invalidateQueries({ queryKey: ['alertas-count'] });
        },
    });
}
